/** The app error boundary (06 §1.8). */
import { t } from '@tarock/i18n';
import { Component, type ComponentChildren } from 'preact';
import { logError } from './log';
import { isChunkLoadError, reloadForStaleBuild } from './staleBuild';

interface State {
  error: unknown;
  id: string;
}

/** A short id the user can quote in a bug report. */
const errorId = (): string => Math.random().toString(36).slice(2, 8).toUpperCase();

/** "Something went wrong" with Reload; chunk-load failures reload once as a stale build. */
export class AppErrorBoundary extends Component<{ children: ComponentChildren }, State> {
  override state: State = { error: null, id: '' };

  override componentDidCatch(error: unknown): void {
    if (isChunkLoadError(error) && reloadForStaleBuild()) {
      return;
    }
    const id = errorId();
    logError(`app ${id}`, error);
    this.setState({ error, id });
  }

  override render() {
    if (this.state.error === null) {
      return this.props.children;
    }
    return (
      <main class="error-screen" role="alert">
        <h1>{t('err.boundary.title')}</h1>
        <p>{t('err.boundary.body')}</p>
        <p class="hint">{t('err.boundary.id', { id: this.state.id })}</p>
        <p>
          <button type="button" class="btn primary" onClick={() => location.reload()}>
            {t('common.reload')}
          </button>
        </p>
      </main>
    );
  }
}
